import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { useDraftMode } from "../contexts/DraftModeContext";
import type { ActivityLogLastChangeResult, GroupedActivityLogChangeResult } from "../utils/models";
import { scheduleWagesKeys } from "./scheduleCache";
import { invalidateSchedule } from "./scheduleCacheWrites";
import { useScheduleCacheEnabled } from "./useScheduleWeekRules";

// The change check keeps reporting a change until the next full reload, so each report is told apart by its count and
// time together. The time alone isn't enough: a change is stamped with its transaction's start, so a save that started
// earlier but committed later raises the count without moving the latest time.
const changeKey = (group: ActivityLogLastChangeResult | undefined): string | null =>
	group && group.changeCount > 0 && group.latestChangeTime ? `${group.changeCount}@${group.latestChangeTime.toMillis()}` : null;

/**
 * When the 20-second change check reports a schedule or wage change, marks the cached weeks stale so the weeks on
 * screen refetch now instead of waiting for the full reload. Each change is handled once. Nothing is refetched in
 * draft mode, where the drafts live in the cached weeks: the check pauses there, but an answer already on its way
 * can still arrive after the draft starts.
 */
export const useInvalidateScheduleOnChange = (status: GroupedActivityLogChangeResult | undefined): void => {
	const queryClient = useQueryClient();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const { isDraftMode } = useDraftMode();
	const enabled = useScheduleCacheEnabled() && !isDraftMode;
	const scheduleChange = changeKey(status?.departmentData);
	const wageChange = changeKey(status?.wage);

	useEffect(() => {
		if (enabled && scheduleChange !== null) void invalidateSchedule(queryClient, departmentId, { allWeeks: true });
	}, [enabled, scheduleChange, departmentId, queryClient]);

	useEffect(() => {
		if (enabled && wageChange !== null) void queryClient.invalidateQueries({ queryKey: scheduleWagesKeys.department(departmentId) });
	}, [enabled, wageChange, departmentId, queryClient]);
};
